import { Button, Flex } from '@chakra-ui/react'
import moment from 'moment'
import React, { useCallback, useEffect } from 'react'
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form'
import { useDispatch } from 'react-redux'
import { toast } from 'react-toastify'

import { formatOffer } from '@/features/offers/helpers/formatOffer'

//Constant
import CollapsibleCard from '@/components/Elements/CollapsibleCard'
import { DatePicker, NumberInput } from '@/components/Form'
import EditableTable from '@/components/Table/EditableTable'
import { OfferStage } from '@/features/offers/constants'
import { getStage } from '@/features/offers/helpers'
import useOffer from '@/features/offers/hooks/useOffer'
import { prepareAndValidateSourcingPostData } from '@/features/offers/offer-detail/helper/sourcing.helper'
import { showSingleSourceProcurementModal } from '@/features/offers/slices/offerModal.slice'
import { SystemVariableType } from '@/features/systemVariables/constants'
import useSystemVariable from '@/features/systemVariables/useSystemVariable'
import useUser from '@/features/users/useUser'
import useVendor from '@/features/vendors/useVendor'
import { showCreateTempVendorModal } from '@/stores/offers/offersSlice'
import { useConfirmationModalManagement } from '@/stores/ui/confirmationModalSlice'
import { hideLoadingOverlay } from '@/stores/ui/loadingOverlaySlice'
import { showModal } from '@/stores/ui/modalSlice'
import { MODAL_TYPES } from '@/utils/constants/modalTypes'
import { isNullish } from '@/utils/helper'
import { deepCopy } from '@/utils/objectHelper'
import { SourcingPostType, TenderDocumentStatus } from '@eapproval/offer-shared'
import { useCostBudgetVendorColumns } from './CostBudgetVendors.columns'
import { useCostBudgetAccess } from '../CostBudgetAccessProvider'

const CostBudgetVendorSection = ({ costBudgetIndex }) => {
  const { canEditVendor } = useCostBudgetAccess()

  const { getValues, setValue } = useFormContext()

  const { append: appendVendor, remove: removeVendor } = useFieldArray({
    name: `sourcing.costBudgets.${costBudgetIndex}.detail.subcontractors`,
  })

  const dispatch = useDispatch()

  const { useSystemVariableOptions } = useSystemVariable()
  const { useFetchAllVendors } = useVendor()
  const { useFetchAllUsers } = useUser()
  const { useUpdateOffer } = useOffer()

  const { open: openConfirmationModal } = useConfirmationModalManagement()
  const updateOffer = useUpdateOffer()

  const { data: fetchVendorResponse } = useFetchAllVendors({
    page: 1,
    pageSize: 0,
    filters: {
      status: ['active', 'temporary'],
    },
  })

  const { data: fetchVendorUserResponse } = useFetchAllUsers({
    page: 1,
    pageSize: 0,
    sortBy: 'fullName',
    orderBy: 1,
    filters: { role: 'vendor', status: 'active' },
  })

  const allVendorUsers = Array.isArray(fetchVendorUserResponse?.data) ? fetchVendorUserResponse?.data : []

  const vendorDetails = useWatch({ name: `sourcing.costBudgets.${costBudgetIndex}.detail.subcontractors` })

  const vendorDueDate = useWatch({ name: `sourcing.costBudgets.${costBudgetIndex}.detail.dueDate` })

  //Transform Variables

  const hasDueDate = !!vendorDueDate
  const passedDueDate = moment(vendorDueDate).isBefore(moment())

  const { data: vendorCategoryOptions } = useSystemVariableOptions(SystemVariableType.VENDOR_CATEGORY, {
    status: 'active',
    valueKey: '_id',
  })

  const updateVendor = useCallback(
    (rowIndex, columnId, value) => {
      console.log(rowIndex, columnId, value)
      setValue(`sourcing.costBudgets.${costBudgetIndex}.detail.subcontractors.${rowIndex}.${columnId}`, value, {
        shouldDirty: true,
      })

      switch (columnId) {
        case 'category': {
          setValue(`sourcing.costBudgets.${costBudgetIndex}.detail.subcontractors.${rowIndex}.vendor`, undefined, {
            shouldDirty: true,
          })
          setValue(`sourcing.costBudgets.${costBudgetIndex}.detail.subcontractors.${rowIndex}.mainContactNumber`, '', {
            shouldDirty: true,
          })
          break
        }
        case 'vendor': {
          if (value === '') {
            setValue(`sourcing.costBudgets.${costBudgetIndex}.detail.subcontractors.${rowIndex}.contactPersons`, [], {
              shouldDirty: true,
            })
            setValue(
              `sourcing.costBudgets.${costBudgetIndex}.detail.subcontractors.${rowIndex}.mainContactNumber`,
              '',
              {
                shouldDirty: true,
              }
            )
          }
        }
      }
    },
    [costBudgetIndex, fetchVendorResponse?.data, setValue]
  )

  useEffect(() => {
    if (!isNullish(vendorDueDate)) {
      //Update All vendor due date if it is empty
      const vendorDetails = getValues(`sourcing.costBudgets.${costBudgetIndex}.detail.subcontractors`)

      if (Array.isArray(vendorDetails)) {
        vendorDetails.forEach((vendorDetail, vendorIndex) => {
          // Only update if the vendor doesn't have a due date yet
          if (isNullish(vendorDetail?.dueDate)) {
            setValue(
              `sourcing.costBudgets.${costBudgetIndex}.detail.subcontractors.${vendorIndex}.dueDate`,
              vendorDueDate,
              { shouldDirty: true }
            )
          }
        })
      }
    }
  }, [vendorDueDate, costBudgetIndex, getValues, setValue])

  const handleApplySingleSourceProcurement = useCallback(() => {
    const costBudgetDetail = getValues(`sourcing.costBudgets.${costBudgetIndex}.detail`)

    if (!costBudgetDetail?._id) {
      toast.error(`Cost Budget ${costBudgetIndex + 1}:  Missing Cost budget detail id`)
      return
    }

    dispatch(
      showSingleSourceProcurementModal({
        costBudgetId: costBudgetDetail._id,
        conditions: costBudgetDetail?.singleSourceProcurementConditions ?? [],
      })
    )
  }, [costBudgetIndex, dispatch, getValues])

  const handleAddVendor = useCallback(
    (e) => {
      e.stopPropagation()
      appendVendor({
        email: '',
        name: '',
        history: [],
        emailLogs: [],
        creditTerm: 90,
        status: 'Draft',
      })
    },
    [appendVendor]
  )

  const handleRemoveVendor = useCallback(
    async (index) => {
      const { isConfirmed } = await openConfirmationModal({
        title: 'Confirm to remove vendor',
        message: 'Filled Information will be lost',
      })
      if (!isConfirmed) {
        //Stop current action, and don't open approval modal
        return
      }

      removeVendor(index)
    },
    [removeVendor]
  )

  const handleOpenVendorDetail = useCallback(
    (vendorIndex) => {
      const costBudgetId = getValues(`sourcing.costBudgets.${costBudgetIndex}.detail._id`)
      const isCostBudgetActive = getValues(`sourcing.costBudgets.${costBudgetIndex}.detail.isActive`)
      const vendorDetail = getValues(`sourcing.costBudgets.${costBudgetIndex}.detail.subcontractors.${vendorIndex}`)
      const costBudgetType = getValues(`sourcing.costBudgets.${costBudgetIndex}.detail.type`)
      const costBudgetScopeOfWork = getValues(`sourcing.costBudgets.${costBudgetIndex}.detail.purpose`)
      const RFQCode = getValues(`sourcing.costBudgets.${costBudgetIndex}.detail.RFQCode`)
      const projectName = getValues('projectName')
      const managerId = getValues('manager')
      const offerId = getValues('_id')
      const currentSourcing = getValues('sourcing')
      const formattedOffer = formatOffer({ sourcing: currentSourcing })
      // Check if scope of work is not empty

      if (!costBudgetScopeOfWork || costBudgetScopeOfWork.trim() === '') {
        toast.error(`Cost Budget ${costBudgetIndex + 1}:  Please enter scope of work before adding quotation`)
        return
      }

      // Check if cost budget type is not empty
      if (!costBudgetType || costBudgetType.trim() === '') {
        toast.error(`Cost Budget ${costBudgetIndex + 1}:  Please select cost budget type before adding quotation`)
        return
      }

      if (!costBudgetId) {
        toast.error(`Cost Budget ${costBudgetIndex + 1}:  Save the cost budget to open the detail`)
        return
      }

      if (!vendorDetail?.vendor) {
        toast.error(`Cost Budget ${costBudgetIndex + 1}:  Please select a vendor before adding quotation`)
        return
      }

      dispatch(
        showModal({
          modalType: MODAL_TYPES.OFFER_VENDOR_DETAIL,
          modalProps: {
            costBudgetId: costBudgetId,
            costBudgetIndex: costBudgetIndex,
            vendorIndex: vendorIndex,
            vendorDetail: deepCopy(vendorDetail),
            offerDetail: formattedOffer,
            isCostBudgetActive: isCostBudgetActive,
            canEdit: canEditVendor,
            costBudgetType,
            RFQCode,
            projectName,
            managerId,
            offerId,
          },
        })
      )
    },
    [canEditVendor, costBudgetIndex, dispatch, getValues]
  )

  const handlePost = useCallback(
    async (selectedVendorIndex = -1) => {
      try {
        const offer = getValues()
        const formattedOffer = formatOffer(offer)
        const offerId = getValues(`_id`)

        const costBudgetDetail = getValues(`sourcing.costBudgets.${costBudgetIndex}.detail`)

        const costBudgetLabel = `Cost Budget ${costBudgetIndex + 1}: `
        const { procurementOfficer, tenderDocumentIndex, vendorDetails, technicalUsers, ccList } =
          prepareAndValidateSourcingPostData({
            offer,
            costBudgetIndex,
            costBudgetDetail,
            selectedVendorIndex,
          })

        await updateOffer.mutateAsync({
          id: offerId,
          data: formattedOffer,
        })
        dispatch(
          showModal({
            modalType: MODAL_TYPES.RFQ,
            modalProps: {
              title: tenderDocumentIndex === 0 ? 'Post New Quotation Request' : `Post Addendum ${tenderDocumentIndex}`,
              offerId: offerId,
              costBudgetId: costBudgetDetail._id,
              tenderDocumentIndex: tenderDocumentIndex,
              vendorDetails: vendorDetails,

              technicalUsers: technicalUsers,
              procurementOfficer: procurementOfficer,
              ccList: ccList,
              requestType: SourcingPostType.REQUEST_FOR_CLARIFICATION,
              canChangePostType: true,
            },
          })
        )
      } catch (res) {
        toast.error(
          res?.message === `${`Cost Budget ${costBudgetIndex + 1}: `}No Active Tender Document / Addendum Found`
            ? `[Unexpected Error] ${res.message}`
            : res?.message
        )
      } finally {
        dispatch(hideLoadingOverlay())
      }
    },
    [costBudgetIndex, dispatch, getValues]
  )

  const handleCreateTempVendor = useCallback(
    (e) => {
      e.stopPropagation()

      dispatch(showCreateTempVendorModal())
    },
    [dispatch]
  )

  const columnDef = useCostBudgetVendorColumns({
    vendorCategoryOptions,
    fetchVendorResponse,
    fetchVendorUserResponse,
    handleOpenVendorDetail,
    handlePost,
    handleRemoveVendor,
    canEditVendor,
    hasDueDate,
    passedDueDate,
  })

  return (
    <CollapsibleCard
      title="Vendor"
      leftElement={
        canEditVendor && (
          <Button colorScheme={'green'} minW="250px" size="sm" onClick={handleApplySingleSourceProcurement}>
            Apply Single Source Procurement
          </Button>
        )
      }
      rightElement={
        canEditVendor && (
          <>
            <Button colorScheme={'green'} onClick={handleCreateTempVendor}>
              Add Temp. Vendor
            </Button>

            <Button colorScheme={'green'} onClick={handleAddVendor}>
              Add
            </Button>
          </>
        )
      }
      defaultIsOpen={true}
    >
      <Flex gap="5" direction={'column'}>
        <Flex gap="2">
          <NumberInput
            label="CTG"
            placeholder="CTG"
            name={`sourcing.costBudgets.${costBudgetIndex}.detail.CTG`}
            isRequired={false}
            isRow
            disabled={!canEditVendor}
          />
          <NumberInput
            label="CNI"
            placeholder="CNI"
            name={`sourcing.costBudgets.${costBudgetIndex}.detail.CNI`}
            isRow
            disabled={!canEditVendor}
            isRequired={false}
          />
          <DatePicker
            name={`sourcing.costBudgets.${costBudgetIndex}.detail.dueDate`}
            label="Vendor Due Date"
            disabled={!canEditVendor}
            isRow
          />
        </Flex>
        <Flex overflowX={'scroll'} mb="5" w="100%">
          <EditableTable
            columnDef={columnDef}
            data={vendorDetails || []}
            updateData={updateVendor}
            disabled={!canEditVendor}
            fixed
          />
        </Flex>
      </Flex>
    </CollapsibleCard>
  )
}

export default CostBudgetVendorSection
